import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Crown,
  Archive,
  Trash2,
  ArrowRight,
  User
} from 'lucide-react';
import { DuplicateGroup } from '@/hooks/useStripeCleanup';

interface DuplicateDetailRowProps {
  group: DuplicateGroup;
}

export function DuplicateDetailRow({ group }: DuplicateDetailRowProps) {
  return (
    <div className="p-4 space-y-4">
      {/* Customer Principal */}
      <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/20">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Crown className="h-4 w-4 text-green-600 dark:text-green-400" />
            Customer Principal (Manter)
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">ID</p>
              <p className="font-mono">{group.keep.id}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Nome</p>
              <p>{group.keep.name || '(sem nome)'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Motivo</p>
              <p className="text-green-600 dark:text-green-400">{group.keep.reason}</p>
            </div>
          </div>
          {group.keep.divus_contact_id && (
            <div className="mt-2 text-sm">
              <span className="text-muted-foreground text-xs">Divus Contact: </span>
              <span className="font-mono text-xs">{group.keep.divus_contact_id}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicates */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <User className="h-4 w-4" />
          Duplicados ({group.merge_from.length})
        </h4>
        
        {group.merge_from.map((dup) => (
          <Card key={dup.id} className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-900/20">
            <CardContent className="py-3">
              <div className="grid grid-cols-4 gap-4 text-sm items-center">
                <div>
                  <p className="text-muted-foreground text-xs">ID</p>
                  <p className="font-mono text-xs">{dup.id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Nome</p>
                  <p>{dup.name || '(sem nome)'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Ação</p>
                  <div className="flex items-center gap-2">
                    {dup.action === 'archive' ? (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                        <Archive className="h-3 w-3 mr-1" />
                        Arquivar
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-600 border-red-600">
                        <Trash2 className="h-3 w-3 mr-1" />
                        Deletar
                      </Badge>
                    )}
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Motivo</p>
                  <p className="text-xs text-muted-foreground">{dup.reason}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions Summary */}
      {group.merge_actions.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-medium mb-2">Ações que serão executadas</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              {group.merge_actions.map((action, index) => (
                <li key={index}>{action}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
